import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, Container, FormControl, Row, Spinner, Table } from 'react-bootstrap';
import {
  ArrowsAngleContract,
  ArrowsAngleExpand,
  Clipboard,
  ClipboardPlus,
  Download,
  Files,
  Pencil,
  PlusCircle
} from 'react-bootstrap-icons';
import { MultiSelect } from 'react-multi-select-component';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CheckBoxInput, TextInput } from '../../form-generator/components';
import DropDownInput from '../../form-generator/components/DropDownInput';
import { ABORT_ERROR, deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import { exportBrowseInventory, fetchBrowseInventory } from '../../helpers/browseInventoryApi';
import { ENDPOINTS, inventoryToggleOptions } from '../../helpers/constants';
import {
  copyInventoryDetails,
  copyToClipboard,
  downloadFileFromBase64,
  downloadFileFromString
} from '../../helpers/global';
import AlertModal from '../AlertModal';
import Loader from '../Loader';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import TextWithEdit from '../TextWithEdit';
import Toggler from '../Toggler';
import FileViewer from '../common/FileViewer';
import FloatingButton from '../common/FloatingButton';
import SortOrderModal from '../common/SortOrderModal';
import SortOrderField, { getRemainingGridColSize } from '../common/SortOrderField';
import QuickToggles from '../common/QuickToggles';
import UnderlineButton from '../common/UnderlineButton';
import CreateSideBar from './CreateSidebar';
import EditInventory from './EditInventory';
import {
  BROWSE_PRESETS,
  COLUMN_LABELS,
  COLUMN_WIDTHS,
  buildBrowseFiltersPayload,
  buildFilterOptions,
  getDefaultSortRules,
  getVisibleColumns,
  sortByOptions
} from './browseInventoryColumns';

const PAGE_SIZE = 20;
const TABLE_MAX_HEIGHT = '65vh';
const SCROLL_BOTTOM_OFFSET = 100;

const InventoryEditSideBar = ({
  editingInventory,
  appChoices,
  onHide,
  onInventoryDelete,
  onInventoryEdit,
  onInventoryFileLoaded,
  refetchChoices
}) => (
  <SlidingSideBar fullScreen visible={!!editingInventory} onClose={onHide} title="Inventory Details">
    {editingInventory && (
      <EditInventory
        inventoryId={editingInventory._id}
        appChoices={appChoices}
        onInventoryFileLoaded={onInventoryFileLoaded}
        onInventoryDelete={onInventoryDelete}
        onInventoryEdit={onInventoryEdit}
        refetchChoices={refetchChoices}
      />
    )}
  </SlidingSideBar>
);

const BrowseInventoryV2 = ({ preset = 'browse', appChoices, refetchChoices }) => {
  const presetConfig = BROWSE_PRESETS[preset] || BROWSE_PRESETS.browse;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalPages: 1,
    resultCount: 0
  });
  const [aggregates, setAggregates] = useState({
    totalReports: 0,
    currentViewTotal: 0,
    stockTotal: 0
  });

  const [statusOptions] = useState(presetConfig.statusOptions.map(v => ({ option: v, value: v })));
  const [status, setSelectedStatus] = useState([...presetConfig.defaultStatus]);

  const filterOptions = useMemo(() => buildFilterOptions(appChoices), [appChoices]);
  const {
    salesLocationOptions,
    manufacturerOptions,
    floorplanLenderOptions,
    bedroomCountOptions,
    exteriorSidingOptions
  } = filterOptions;

  const [salesLocation, setSelectedSalesLocation] = useState(filterOptions.salesLocationDefaults);
  const [manufacturer, setSelectedManufacterer] = useState(filterOptions.manufacturerDefaults);
  const [floorplanLender, setSelectedFloorplanLender] = useState(filterOptions.floorplanLenderDefaults);
  const [bedRoomCount, setBedRoomCount] = useState(filterOptions.bedRoomCountDefaults);
  const [exteriorSiding, setExteriorSiding] = useState(filterOptions.exteriorSidingDefaults);

  const [sortRules, setSortRules] = useState(getDefaultSortRules);
  const [showSortModal, setShowSortModal] = useState(false);
  const [excludedInventories, setExcludedInventories] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [showUnsoldContractedUnits, setShowUnsoldContractedUnit] = useState(false);
  const [showCurtailmentUnitsOnly, setShowCurtailmentUnitsOnly] = useState(false);
  const [createSidebarMeta, setCreateSidebarMeta] = useState(null);
  const [fileViewerActiveUnit, setFileViewerActiveUnit] = useState(null);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);
  const [quickSearchQuery, setQuickSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [toggling, setToggling] = useState(false);
  const [editingInventory, setEditingInventory] = useState(null);

  const fetchAbortRef = useRef(null);
  const tableContainerRef = useRef(null);
  const isFetchingRef = useRef(false);

  const debouncedSetSearch = useRef(
    debounce(value => {
      setDebouncedSearchQuery(value);
      setCurrentPage(1);
      setHasMoreData(true);
    }, 300)
  ).current;

  useEffect(() => {
    debouncedSetSearch(quickSearchQuery.trim());
    return () => debouncedSetSearch.cancel();
  }, [quickSearchQuery, debouncedSetSearch]);

  const filtersPayload = useMemo(
    () =>
      buildBrowseFiltersPayload({
        preset,
        status,
        salesLocation,
        salesLocationOptions,
        manufacturer,
        manufacturerOptions,
        floorplanLender,
        floorplanLenderOptions,
        bedRoomCount,
        bedroomCountOptions,
        exteriorSiding,
        exteriorSidingOptions,
        showUnsoldContractedUnits,
        showCurtailmentUnitsOnly
      }),
    [
      preset,
      status,
      salesLocation,
      salesLocationOptions,
      manufacturer,
      manufacturerOptions,
      floorplanLender,
      floorplanLenderOptions,
      bedRoomCount,
      bedroomCountOptions,
      exteriorSiding,
      exteriorSidingOptions,
      showUnsoldContractedUnits,
      showCurtailmentUnitsOnly
    ]
  );

  const includeContractHeaders = rows.some(unit => unit.contract);
  const includeContactHeaders = ['Stock', 'RSO'].some(s => status.includes(s));
  const visibleColumns = useMemo(
    () =>
      getVisibleColumns({
        preset,
        includeContractHeaders,
        includeContactHeaders
      }),
    [preset, includeContractHeaders, includeContactHeaders]
  );

  const fetchBrowsePage = useCallback(
    async (page = 1, append = false) => {
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
      const controller = new AbortController();
      fetchAbortRef.current = controller;
      isFetchingRef.current = true;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const { response, error } = await fetchBrowseInventory({
        preset,
        filters: filtersPayload,
        query: debouncedSearchQuery,
        sortRules,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        signal: controller.signal
      });

      if (error) {
        isFetchingRef.current = false;
        if (error !== ABORT_ERROR) {
          toast.error(error);
        }
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
        return;
      }

      const nextPagination = response?.pagination || {
        pageNumber: 1,
        pageSize: PAGE_SIZE,
        totalPages: 1,
        resultCount: 0
      };
      const nextResults = response?.results || [];

      if (append) {
        setRows(prev => [...prev, ...nextResults]);
      } else {
        setRows(nextResults);
      }

      setPagination(nextPagination);
      setAggregates(
        response?.aggregates || {
          totalReports: 0,
          currentViewTotal: 0,
          stockTotal: 0
        }
      );
      setCurrentPage(page);
      setHasMoreData(page < (nextPagination.totalPages || 1));

      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      isFetchingRef.current = false;
    },
    [preset, filtersPayload, debouncedSearchQuery, sortRules]
  );

  useEffect(() => {
    if (!salesLocationOptions.length) {
      return;
    }
    setCurrentPage(1);
    setHasMoreData(true);
    fetchBrowsePage(1, false);
  }, [fetchBrowsePage, salesLocationOptions.length]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && !loading && hasMoreData && !isFetchingRef.current) {
      fetchBrowsePage(currentPage + 1, true);
    }
  }, [loadingMore, loading, hasMoreData, currentPage, fetchBrowsePage]);

  useEffect(() => {
    const checkBottom = () => {
      if (loadingMore || loading || isFetchingRef.current) {
        return;
      }

      const element = tableContainerRef.current;
      if (!element) {
        return;
      }

      const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      if (distanceFromBottom <= SCROLL_BOTTOM_OFFSET) {
        handleLoadMore();
      }
    };

    const debouncedHandleScroll = debounce(checkBottom, 100);
    const element = tableContainerRef.current;

    if (element) {
      element.addEventListener('scroll', debouncedHandleScroll);
      checkBottom();
      return () => {
        element.removeEventListener('scroll', debouncedHandleScroll);
        debouncedHandleScroll.cancel();
      };
    }
  }, [handleLoadMore, loadingMore, loading, rows.length]);

  const refreshBrowseData = useCallback(() => {
    setCurrentPage(1);
    setHasMoreData(true);
    fetchBrowsePage(1, false);
  }, [fetchBrowsePage]);

  const resetToFirstPage = updater => {
    setCurrentPage(1);
    setHasMoreData(true);
    updater();
  };

  const runExport = async exportType => {
    if (!aggregates.totalReports) {
      return toast.error('No rows to export');
    }

    setSubmitting(true);
    const salesLocationChoices = appChoices.find(c => c.key === 'salesLocation')?.values || [];
    const { response, error } = await exportBrowseInventory({
      preset,
      filters: filtersPayload,
      query: debouncedSearchQuery,
      sortRules,
      exportType,
      excludedIds: excludedInventories,
      hiddenColumns,
      salesLocationChoices
    });
    setSubmitting(false);

    if (error) {
      return toast.error(error);
    }

    if (exportType === 'csv') {
      downloadFileFromString({ data: response.data, fileName: response.fileName });
      toast.success('CSV downloaded');
      return;
    }

    if (exportType === 'copy') {
      if (!response.text) {
        return toast.error('No rows to copy');
      }
      copyToClipboard(response.text);
      toast.success('All rows copied to clipboard');
      return;
    }

    if (exportType === 'pdf') {
      toast.success('Document created successfully, file is being downloaded...');
      downloadFileFromBase64({
        fileName: response.fileName,
        data: response.data,
        mimeType: response.mimeType
      });
    }
  };

  const onExcludeInventoryChange = (id, checked) => {
    setExcludedInventories(prev => {
      if (checked) {
        return prev.filter(item => item !== id);
      }
      return prev.includes(id) ? prev : [...prev, id];
    });
  };

  const toggleColumnVisibility = columnKey => {
    setHiddenColumns(prev => (prev.includes(columnKey) ? prev.filter(key => key !== columnKey) : [...prev, columnKey]));
  };

  const openFileViewer = async unit => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_SEARCH_NEW,
      requestBody: {
        filter: { _id: unit._id },
        populate: true,
        includeContract: false,
        includeContact: false,
        returnOnlyInventoryInStock: false,
        pageSize: 1
      }
    });

    if (error) {
      return toast.error(error);
    }

    const fullUnit = response?.results?.[0];
    if (!fullUnit) {
      return toast.error('Unable to load files for this unit');
    }
    setFileViewerActiveUnit(fullUnit);
  };

  const onInvoiceAmountSubmit = async (inventory, amount) => {
    const parsedAmount = Number(amount);
    toast.info('Please wait updating amount...');

    const { error } = await makeApiRequests({
      requestBody: { invoice: parsedAmount },
      method: 'PUT',
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventory._id)
    });

    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Invoice updated successfully');
    refreshBrowseData();
  };

  const toggleInventory = async (inventoryId, label, key, value) => {
    setToggling(inventoryId);
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(inventoryId),
      method: 'PUT',
      requestBody: { [key]: value }
    });
    setToggling(null);

    if (error) {
      toast.error(error);
      return;
    }

    toast.success(`${label} updated successfully!`);
    setRows(prev => prev.map(row => (row._id === response._id ? { ...row, ...response } : row)));
  };

  const updateContract = (contract, field, value) => {
    setRows(prev =>
      prev.map(row =>
        row.contract?._id === contract._id ? { ...row, contract: { ...row.contract, [field]: value } } : row
      )
    );
  };

  const deleteFile = async () => {
    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = fileViewerActiveUnit.files.filter(f => f._id !== toBeDeletedFile._id).map(f => f._id);
    const { error } = await makeApiRequests({
      requestBody: { files: newFileArray },
      endpoint: ENDPOINTS.INVENTORY_WITH_ID(fileViewerActiveUnit._id),
      method: 'PUT'
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setDeleteFileModalMeta(null);
    deleteFileFromServer(toBeDeletedFile);
    openFileViewer(fileViewerActiveUnit);
  };

  const filterDropdowns = [
    {
      title: 'Inventory Status',
      value: status,
      options: statusOptions,
      onChange: value => resetToFirstPage(() => setSelectedStatus(value)),
      multiple: true
    },
    {
      title: 'Unit Location',
      value: salesLocation,
      options: salesLocationOptions,
      onChange: value => resetToFirstPage(() => setSelectedSalesLocation(value)),
      multiple: true
    },
    {
      title: 'Manufacturer',
      value: manufacturer,
      options: manufacturerOptions,
      onChange: value => resetToFirstPage(() => setSelectedManufacterer(value)),
      multiple: true
    },
    {
      title: 'Floorplan Lender',
      value: floorplanLender,
      options: floorplanLenderOptions,
      onChange: value => resetToFirstPage(() => setSelectedFloorplanLender(value)),
      multiple: true
    }
  ];

  if (presetConfig.filterKeys.includes('bedRooms')) {
    filterDropdowns.push({
      title: 'Bedroom Count',
      value: bedRoomCount,
      options: bedroomCountOptions,
      onChange: value => resetToFirstPage(() => setBedRoomCount(value)),
      multiple: true
    });
  }

  if (presetConfig.filterKeys.includes('exteriorSiding')) {
    filterDropdowns.push({
      title: 'Exterior Siding',
      value: exteriorSiding,
      options: exteriorSidingOptions,
      onChange: value => resetToFirstPage(() => setExteriorSiding(value)),
      multiple: true
    });
  }

  const sortColSize = getRemainingGridColSize(filterDropdowns.map(dropdown => dropdown.col || 3));

  const renderCell = (columnKey, unit) => {
    switch (columnKey) {
      case 'inventoryStatus':
        return unit.inventoryStatus || '-';
      case 'contractedBuyer':
        return unit.contract ? (
          <Link target="_blank" to={`/contract/${unit.contract._id}`}>
            {unit.contract.buyer}
          </Link>
        ) : (
          ''
        );
      case 'contractStatus':
        return unit.contract ? (
          <Toggler
            id={unit.contract._id}
            fieldName="status"
            fieldLabel="Status"
            value={unit.contract.status}
            options={appChoices.find(ac => ac.key === 'Status')?.values || []}
            onFieldUpdate={(field, value) => updateContract(unit.contract, field, value)}
          />
        ) : (
          ''
        );
      case 'potentialBuyers':
        return unit.contacts?.length
          ? unit.contacts.map((contact, index) => (
              <React.Fragment key={contact._id}>
                <Link target="_blank" to={`/crm/${contact._id}`}>
                  {contact.buyer}
                </Link>
                {index + 1 !== unit.contacts.length && ', '}
              </React.Fragment>
            ))
          : '';
      case 'unitLocation':
        return unit.unitLocation?.location || '-';
      case 'manufacturer':
        return unit.manufacturer?.factoryShortName || '-';
      case 'model':
        return unit.model ? (
          <div className="d-flex flex-column">
            <span>{unit.model.modelName || '-'}</span>
            <span className="text-muted small">{unit.model.modelNumber || ''}</span>
          </div>
        ) : (
          '-'
        );
      case 'maxSalesPrice':
        return `$${unit.maxSalesPrice || '-'}`;
      case 'serials':
        return `${unit.serialA || ''}${unit.serialB ? `/${unit.serialB}` : ''}`;
      case 'sizeOverall':
        return unit.sizeOverall;
      case 'bedRooms':
        return unit.bedRooms;
      case 'bathRooms':
        return unit.bathRooms;
      case 'floorplanLender':
        return unit.floorplanLender?.name || '-';
      case 'dateOfManufacture':
        return unit.dateOfManufacture ? moment(unit.dateOfManufacture).format('MMMM Do YYYY') : 'N/A';
      case 'invoiceDate':
        return unit.invoiceDate ? moment(unit.invoiceDate).format('MMMM Do YYYY') : 'N/A';
      case 'invoice':
        return (
          <TextWithEdit
            preFix="$"
            text={unit.invoice || 0}
            onSubmit={value => onInvoiceAmountSubmit(unit, value)}
            required
            onRequiredInvalidation={() => toast.error('Invoice Amount is required!')}
          />
        );
      case 'slot':
        return presetConfig.features.quickToggles ? (
          <QuickToggles
            data={unit}
            toggleOptions={inventoryToggleOptions({
              inventory: unit,
              appChoices,
              includeManufacturer: false
            })}
            onToggle={(label, key, value) => toggleInventory(unit._id, label, key, value)}
            toggling={toggling === unit._id}
            showLabel={false}
          />
        ) : (
          unit.slotNumber || 'Unassigned'
        );
      default:
        return '-';
    }
  };

  const tableColumnCount = visibleColumns.length + 2;

  return (
    <Container fluid className="h-100 pt-2 pb-3 px-md-2">
      <Card className="border rounded">
        <Card.Header className="bg-primary">
          <h5 className="mb-0 text-white">{presetConfig.title}</h5>
        </Card.Header>
        <Card.Body className="p-2">
          <Row>
            {filterDropdowns.map(dropdownMeta => (
              <Col key={dropdownMeta.title} xs={12} md={dropdownMeta.col || 3} className="px-4 mt-3">
                <h6 className="mb-2">{dropdownMeta.title}</h6>
                <MultiSelect
                  showCheckbox
                  closeOnChangedValue={false}
                  className="midFont"
                  valueRenderer={(selected, options) =>
                    selected.length === options.length
                      ? 'All Selected'
                      : selected
                          .map(s => dropdownMeta.options.find(o => o.value === s.value)?.option || 'N/A')
                          .join(', ')
                  }
                  value={dropdownMeta.value.map(o => ({ label: o, value: o }))}
                  options={dropdownMeta.options.map(o => ({ label: o.option, value: o.value }))}
                  onChange={list => dropdownMeta.onChange(list.map(o => o.value))}
                />
              </Col>
            ))}

            <Col xs={12} md={sortColSize} className="px-4 mt-3">
              <SortOrderField
                sortRules={sortRules}
                sortFields={sortByOptions}
                onClick={() => setShowSortModal(true)}
                disabled={loading}
              />
            </Col>

            {presetConfig.features.showUnsoldContractedUnits && !status.includes('Contracted') && (
              <Col xs={12} md={4} className="mt-4 px-4">
                <CheckBoxInput
                  size="sm"
                  onChangeFunction={e => e && resetToFirstPage(() => setShowUnsoldContractedUnit(e.target.checked))}
                  checked={showUnsoldContractedUnits}
                  label="Show Undelivered Contracted Units"
                  showLabel
                />
              </Col>
            )}

            {presetConfig.features.showCurtailmentToggle && (
              <Col xs={12} md={!status.includes('Contracted') ? 4 : 8} className="mt-4 px-4">
                <CheckBoxInput
                  size="sm"
                  onChangeFunction={e => e && resetToFirstPage(() => setShowCurtailmentUnitsOnly(e.target.checked))}
                  checked={showCurtailmentUnitsOnly}
                  label="Show Curtailments Only"
                  showLabel
                />
              </Col>
            )}

            <Col xs={12} md={4} className="mt-4 text-right px-4">
              <Button
                size="sm"
                className="mr-2"
                onClick={() => runExport('csv')}
                disabled={submitting || !aggregates.totalReports}
              >
                <Download />
                <span className="align-middle ml-2">Download CSV</span>
              </Button>
              <Button size="sm" onClick={() => runExport('pdf')} disabled={submitting || !aggregates.totalReports}>
                Print
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <hr />

      <div className="d-flex align-items-center">
        <div className="d-flex gap-2 flex-grow-1 align-items-center">
          <h6 className="mb-0">Inventory:</h6>
          {presetConfig.features.copyAllRows && (
            <UnderlineButton
              onClick={() => runExport('copy')}
              variant="success"
              text="Copy all rows"
              disabled={submitting || !aggregates.totalReports}
              Icon={Clipboard}
              iconSize={14}
            />
          )}
        </div>
        <div>
          <h6 className="text-right text-muted smallFont mb-0">
            Total Reports: <span className="text-dark mr-4">{aggregates.totalReports}</span>
            Current View Total:{' '}
            <span className="text-dark mr-4">${Number(aggregates.currentViewTotal || 0).toFixed(2)}</span>
            Stock Total: <span className="text-dark mr-2">${Number(aggregates.stockTotal || 0).toFixed(2)}</span>
          </h6>
        </div>
      </div>

      <div className="input-group my-3 px-1">
        <TextInput
          id="searchText"
          hint="Quick Search..."
          value={quickSearchQuery}
          onChange={e => setQuickSearchQuery(e.target.value)}
          size="sm"
        />
      </div>

      {loading ? (
        <Loader />
      ) : (
        <>
          <div ref={tableContainerRef} style={{ maxHeight: TABLE_MAX_HEIGHT, overflowY: 'auto', overflowX: 'auto' }}>
            <Table style={{ fontSize: 12 }} bordered className="text-center mb-0">
              <thead>
                <tr className="bg-dark text-light">
                  <th style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#343a40' }}>Print?</th>
                  <th style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#343a40', minWidth: 140 }}>
                    Action
                  </th>
                  {visibleColumns.map(columnKey => (
                    <th
                      key={columnKey}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        backgroundColor: '#343a40',
                        minWidth:
                          columnKey === 'contractedBuyer' || columnKey === 'potentialBuyers'
                            ? 120
                            : COLUMN_WIDTHS[columnKey]
                      }}
                    >
                      <div className="hover-dark" onClick={() => toggleColumnVisibility(columnKey)}>
                        {!hiddenColumns.includes(columnKey) ? (
                          <ArrowsAngleContract size={10} />
                        ) : (
                          <ArrowsAngleExpand size={10} className="mx-1" />
                        )}
                      </div>
                      {!hiddenColumns.includes(columnKey)
                        ? COLUMN_LABELS[columnKey]
                        : `${COLUMN_LABELS[columnKey].slice(0, 3)}...`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map(unit => (
                    <tr key={unit._id}>
                      <td>
                        <FormControl
                          checked={!excludedInventories.includes(unit._id)}
                          style={{ width: 16, height: 16 }}
                          type="checkbox"
                          onChange={e => onExcludeInventoryChange(unit._id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <Pencil className="hover-light mr-2" onClick={() => setEditingInventory(unit)} />
                        <Clipboard
                          className="hover-light mr-2"
                          onClick={() => {
                            copyInventoryDetails(unit);
                            toast.success('Copied!');
                          }}
                        />
                        <Files className="hover-light mr-2" onClick={() => openFileViewer(unit)} />
                        <ClipboardPlus
                          className="hover-light mr-2"
                          onClick={() =>
                            setCreateSidebarMeta({
                              prefillUnit: {
                                ...unit,
                                serialA: '',
                                serialB: '',
                                serialC: '',
                                hudA: '',
                                hudB: '',
                                hudC: ''
                              },
                              isDuplicating: true
                            })
                          }
                        />
                      </td>
                      {visibleColumns.map(columnKey => (
                        <td key={columnKey}>{hiddenColumns.includes(columnKey) ? '' : renderCell(columnKey, unit)}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColumnCount}>No Inventories to show</td>
                  </tr>
                )}
                {loadingMore && (
                  <tr>
                    <td colSpan={tableColumnCount} className="py-3">
                      <div className="d-flex justify-content-center align-items-center text-muted">
                        <Spinner animation="border" size="sm" className="mr-2" />
                        <span>Loading more...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {pagination.resultCount > 0 && (
            <div className="text-muted small mt-2">
              Showing <span className="text-dark font-weight-bold">{rows.length}</span> of{' '}
              <span className="text-dark font-weight-bold">{pagination.resultCount}</span> units
              {!hasMoreData && rows.length > 0 ? ' (all loaded)' : ''}
            </div>
          )}
        </>
      )}

      <FileViewer
        show={fileViewerActiveUnit !== null}
        onHide={() => setFileViewerActiveUnit(null)}
        files={fileViewerActiveUnit ? fileViewerActiveUnit.files : []}
        driveFolder={fileViewerActiveUnit ? fileViewerActiveUnit.driveFolder : []}
        onFileDeleteClick={file => setDeleteFileModalMeta({ file })}
      />

      <AlertModal
        show={deleteFileModalMeta !== null}
        alertText={`Are you sure to delete this file (${deleteFileModalMeta?.file?.metadata?.originalName ||
          deleteFileModalMeta?.file?.filename ||
          ''}). You will lose access to this file permanently?`}
        showProgress={deleteFileModalMeta?.deletingFile}
        progressText="Deleting file..."
        continueButtonText="Delete"
        onDismissClick={() => setDeleteFileModalMeta(null)}
        onHide={() => setDeleteFileModalMeta(null)}
        onContinueClick={deleteFile}
      />

      <InventoryEditSideBar
        appChoices={appChoices}
        onHide={() => setEditingInventory(null)}
        editingInventory={editingInventory}
        onInventoryEdit={() => {
          setEditingInventory(null);
          refreshBrowseData();
        }}
        onInventoryDelete={() => {
          setEditingInventory(null);
          refreshBrowseData();
        }}
        onInventoryFileLoaded={refreshBrowseData}
        refetchChoices={refetchChoices}
      />

      {presetConfig.features.addUnit && (
        <>
          <CreateSideBar
            onInventoryAdd={() => {
              setCreateSidebarMeta(null);
              refreshBrowseData();
            }}
            appChoices={appChoices}
            show={createSidebarMeta}
            onHide={() => setCreateSidebarMeta(null)}
            prefillUnit={createSidebarMeta?.prefillUnit}
            isDuplicating={createSidebarMeta?.prefillUnit}
            refetchChoices={refetchChoices}
          />
          <FloatingButton
            variant="success"
            text="Add New Unit"
            Icon={PlusCircle}
            onClick={() => setCreateSidebarMeta({})}
          />
        </>
      )}

      <SortOrderModal
        show={showSortModal}
        onHide={() => setShowSortModal(false)}
        title={preset === 'curtailments' ? 'Sort Curtailments' : 'Sort Inventory'}
        sortFields={sortByOptions}
        value={sortRules}
        defaultField="unitLocation"
        defaultDirection="asc"
        onApply={rules => {
          resetToFirstPage(() => setSortRules(rules));
          setShowSortModal(false);
        }}
      />
    </Container>
  );
};

export default BrowseInventoryV2;
